import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LevelList } from '../components/worlds/LevelList';
import { WorldIcon } from '../components/worlds/WorldIcon';
import { useGame } from '../context/GameContext';
import { getWorldId, loadWorldsManifest } from '../services/worldLoader';
import { Spinner } from '../components/common/Spinner';
import { Button } from '../components/common/Button';
import { matchesLevelKey } from '../utils/ids';
import { findWorldCategory } from '../utils/worldCategories';
import { getCatalogText } from '../utils/catalogText';
import type { World, WorldCategory, WorldManifestEntry } from '../types/world';

export function WorldDetailPage() {
  const { worldId } = useParams<{ worldId: string }>();
  const navigate = useNavigate();
  const { gameData, isWorldUnlocked, loadWorldData } = useGame();
  const [world, setWorld] = useState<World | null>(null);
  const [category, setCategory] = useState<WorldCategory>();
  const [worldInfo, setWorldInfo] = useState<WorldManifestEntry>();
  const [loading, setLoading] = useState(true);
  const text = getCatalogText();

  useEffect(() => {
    let cancelled = false;
    async function fetchWorld() {
      if (!worldId) return;
      setLoading(true);
      setWorld(null);
      setCategory(undefined);
      setWorldInfo(undefined);
      
      try {
        const manifest = await loadWorldsManifest();
        const loadedWorld = await loadWorldData(worldId, manifest);
        if (cancelled) return;
        if (loadedWorld) {
          setWorld(loadedWorld);
          setCategory(findWorldCategory(manifest, worldId));
          setWorldInfo(manifest.worlds.find(entry => getWorldId(entry) === worldId));
        }
      } catch (error) {
        console.error('Failed to load world:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    
    fetchWorld();
    return () => { cancelled = true; };
  }, [worldId, loadWorldData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!world) {
    return <div className="mx-auto max-w-7xl px-4 py-8"><div role="alert" className="mb-6 rounded-xl border border-gray-200 bg-white p-8 dark:border-gray-700 dark:bg-gray-800"><h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{text.worldNotFound}</h1><p className="mt-3 text-gray-600 dark:text-gray-300">{text.worldNotFoundDescription}</p></div><Button variant="secondary" onClick={() => navigate('/worlds')}>← {text.backToWorlds}</Button></div>;
  }

  const isUnlocked = worldInfo?.alwaysUnlocked || isWorldUnlocked(world.id, world);
  const completedLevels = gameData?.progress.completedLevels || [];
  const worldCompletedLevels = completedLevels.filter((id) =>
    world.levels.some(lvl => matchesLevelKey(id, world.id, lvl.id))
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="mb-6">
        <Button onClick={() => navigate(category ? `/worlds/category/${category.id}` : '/worlds')} variant="secondary" className="mb-4">
          ← {category ? text.backToCategory(category.name) : text.backToWorlds}
        </Button>
        <div className="flex items-center space-x-3 mb-4">
          <span className="text-primary-600 dark:text-primary-400"><WorldIcon name={world.icon} size={36} /></span>
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">{world.name}</h1>
            <p className="text-gray-600 dark:text-gray-300">{world.description}</p>
          </div>
        </div>
        {world.estimatedHours && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Estimated time: {world.estimatedHours} hours</p>
        )}
      </div>

      {!isUnlocked ? (
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-600 dark:text-gray-300 mb-4">This world is locked.</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Complete the previous worlds to unlock this one.
          </p>
        </div>
      ) : (
        <LevelList
          levels={world.levels}
          completedLevels={worldCompletedLevels}
          worldId={world.id}
        />
      )}
    </div>
  );
}
